/*
 * Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

const useStyles =makeStyles<Theme>((theme: Theme) =>
  createStyles({
    topicContainer: {
      width: theme.spacing(140),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderBottom: 0,
    },
    topicTypeContainer: {
      height: theme.spacing(2),
      display: 'flex',
      marginBottom: '4px',
      alignContent: 'center',
    },
    typeChipContainer: {
      display: 'flex',
      height: theme.spacing(2.5),
      marginRight: theme.spacing(1),
      alignItems: 'center',
    },
    pubTypeChipContainer: {
      backgroundColor: '#0095FF',
      color: '#FFFFFF',
      borderRadius: 8
    },
    subTypeChipContainer: {
      backgroundColor: '#05A26B',
      color: '#FFFFFF',
      borderRadius: 8
    },
    accordion: {
      boxShadow: 'none',
      borderRadius: theme.spacing(1.5),
    },
    accordionSummary: {
      maxHeight: theme.spacing(6),
      minHeight: '48px !important',
    },
    endpointContainer: {
      display: 'flex',
      padding: theme.spacing(1, 1),
      border: 1,
      borderTop: `1px solid ${theme.palette.grey[200]}`,
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      gap: theme.spacing(1),
    },
    textInput: {
      width: theme.spacing(140),
      maxWidth: theme.spacing(124),
    },
    ConnectButton: {
      borderRadius: theme.spacing(1),
      padding: theme.spacing(1, 2),
      fontSize: 13,
      fontWeight: 500,
      textTransform: 'initial',
    },
    copyTextField: {
      maxWidth: theme.spacing(148),
      height: theme.spacing(5),
    },
    deleteButton: {
      color: theme.palette.error.main,
      textTransform: "initial",
      fontSize: 13,
      fontWeight: 400
    },
    clearOutput: {
      textTransform: "initial",
      fontSize: 13,
      fontWeight: 400
    },
    payloadContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: theme.spacing(13.75),
      borderRadius: 'none',
      marginBottom: theme.spacing(8),
      marginLeft: theme.spacing(1),
    },
    sendIcon: {
      marginBottom: theme.spacing(2),
      marginLeft: theme.spacing(1),
    },
    containerButton: {
      float: 'left',
      marginLeft: theme.spacing(1.5),
      borderRadius: theme.spacing(1),
      fontSize: 13,
      fontWeight: 500,
      textTransform: 'initial',
      marginBottom: theme.spacing(1),
    },
    parameterContainerWrapper: {
      width: '100%',
      height: theme.spacing(27.5),
      overflow: 'scroll',
    },
    parameterContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: theme.spacing(1),
      width: '100%',
      height: theme.spacing(22),
      borderRadius: 'none',
      overflow: 'scroll',
    },
    paramTextInput: {
      size: 'small',
      width: theme.spacing(30),
      marginTop: theme.spacing(0.5),
    },
    executeButton: {
      marginLeft: theme.spacing(0),
      marginBottom: theme.spacing(1),
      alignContent: 'left',
    },
    outputConsoleContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      width: "100%",
      padding: theme.spacing(1, 2),
      alignItems: 'center',
      borderTop: `1px solid ${theme.palette.grey[200]}`,
    },
    outputLabel: {
      display: 'flex',
      justifyContent: 'center',
      fontSize: 13,
      fontWeight: 400
    },
    outputButtons: {
      display: 'flex',
      justifyContent: 'end',
      gap: theme.spacing(2),
    },
    outputContainer: {
      height: theme.spacing(20),
      width: '100%',
      overflow: 'scroll',
      borderTop: `1px solid ${theme.palette.grey[200]}`,
      borderBottomRightRadius: theme.spacing(1),
      borderBottomLeftRadius: theme.spacing(1),
      padding: theme.spacing(1, 2),
    },
    accordinDetails: {
      padding: 0,
    },
    logColor1: {
      color: "#00008B",
    },
    logColor2: {
      color: "#7B68EE",
    },
    logColor3: {
      color: "#008000",
    },
    logColor4: {
      color: "#8A2BE2",
    },
    logColor5: {
      color: "#9370DB",
    },
    infoLogs: {
      color: "blue",
      lineHeight: theme.spacing(0.25),
    },
    errorLogs: {
      color: "red",
      lineHeight: theme.spacing(0.25),
    },
    toolTipParent: {
      display: 'inline-flex',
      lineHeight: theme.spacing(0.25),
      '& $toolTipChild': {
        visibility: 'hidden',
      },
      '&:hover': {
        '& $toolTipChild': {
          visibility: 'visible',
          animation: '$animFade 250ms 1',
        },
      },
    },
    toolTipChild: {
      top: theme.spacing(-4),
      padding: theme.spacing(0.5),
      color: theme.palette.primary.main,
      display: 'flex',
      background: theme.palette.common.white,
      boxShadow: `0 1px 3px rgba(0, 0, 0, 0.19)`,
      userSelect: 'none',
      borderRadius: theme.spacing(0.625),
      position: 'absolute',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.common.white,
      },
    },
    logMsg: {
      lineHeight: theme.spacing(0.25),
      display: 'inline',
      overflow: 'visible',
      whiteSpace: 'nowrap',
      '&:hover': {
        '& $actionContainer': {
          visibility: 'visible',
          animation: '$animFade 250ms 1',
          backgroundColor: theme.palette.common.white,
        },
      },
    },
    infoSection: {
      display: 'inline',
      whiteSpace: 'nowrap',
    },
    logLineWrapper: { display: 'flex' },
    logContextDivider: {
      color: theme.palette.divider,
    },
    logContextKeyValue: {
      color: "blue",
    },
    disableUserSelect: {
      userSelect: 'none',
    },
    logEntryActions: {
      position: 'relative',
      display: 'flex',
      paddingRight: theme.spacing(1.25),
    },
    actionContainer: {
      top: theme.spacing(-6),
      padding: theme.spacing(0.5),
      display: 'flex',
      width: 'max-content',
      background: theme.palette.common.white,
      position: 'absolute',
      cursor: 'pointer',
      border: `1px solid ${theme.palette.grey[100]}`,
      borderColor: theme.palette.grey[100],
      borderRadius: theme.spacing(0.625),
      backgroundColor: theme.palette.common.white,
      boxShadow: `0 1px 3px rgba(0, 0, 0, 0.19)`,
    },
    tabRoot: {
      borderBottom: "1px solid #E4E8EE",
      marginBottom: "21px",
      height: "32px",
    },
    tabIndicator: {
      color: "#5567d5",
      backgroundColor: "#5567d5",
    },
    tab: {
     padding: 0,
     fontFamily: "GilmerMedium",
     color: "#1D2028",
    },
    tabTitleText: {
      color: theme.custom.grey,
      fontSize: 13,
      fontWeight: 500,
      textTransform: 'initial',
      alignContent: 'start',
      flexWrap:'wrap',
      marginLeft: theme.spacing(2)
    },
  })
);

export default useStyles;
